/**
 * Airport Service
 *
 * Loads the operating airports from `/config/airports`. Pure fetcher — the
 * response is cached in react-query (see hooks/useAirports.js) and the
 * synchronous detection helpers in utils/airports.js read it from that cache.
 *
 * Each airport carries `location` (lat/lng/address/city/country/postal_code),
 * its `terminals`, and `waiting_times` — four buckets
 * (from|to)_airport_(local|international), each `{label, items[], total_minutes}`.
 * `total_minutes` is server-computed; never sum `items` client-side.
 */

import { customersApiClient } from './apiClient';

const API_ENDPOINTS = {
  AIRPORTS: '/config/airports',
};

export const airportService = {
  /**
   * Fetch every airport in one shot.
   *
   * The endpoint is paginated (default per_page 15) but this is reference data
   * the detector needs complete — a partial page would silently stop matching
   * the airports on page 2 — so ask for a page large enough to hold the list.
   *
   * @returns {Promise<{success: boolean, data: Array|null, error: string|null}>}
   */
  async getAirports() {
    const result = await customersApiClient.get(`${API_ENDPOINTS.AIRPORTS}?per_page=100`);

    // apiClient already unwrapped {status,key,message,data} → result.data is the
    // inner payload: {airports: [...], pagination: {...}}.
    if (result.success && Array.isArray(result.data?.airports)) {
      return { success: true, data: result.data.airports, error: null };
    }

    return {
      success: false,
      data: null,
      error: result.error || 'Failed to fetch airports',
    };
  },
};

export default airportService;
